import { useAccount } from "@qw/auth";

import { KnownInboxLabel } from "@qw/qw-common";
import type { Icon, MaterialSymbol } from "@qw/ui";
import { type ComponentProps, useCallback, useMemo, useSyncExternalStore } from "react";
import { latestThreadMessageId } from "../conversation/latest-thread-message";
import type { MaybePartialInboxConversation } from "../types";
import { useAddLabelToConversation } from "./use-add-label-to-conversation";
import { useCreateForwardDraft } from "./use-create-forward-draft";
import { useCreateReplyDraft } from "./use-create-reply-draft";
import { useDeleteConversation } from "./use-delete-conversation";
import { ReplyPlacement } from "./use-drafts-compose";
import { useRemoveLabelFromConversation } from "./use-remove-label-from-conversation";
import { useSetThreadReadState } from "./use-set-thread-read-state";

export type ConversationMenuItemId =
  | "reply"
  | "reply-all"
  | "forward"
  | "inbox"
  | "delete"
  | "read-state"
  | "star"
  | "open-in-gmail";

type ConversationMenuPendingGroup = "draft" | "label" | "delete" | "read";

interface ButtonConversationMenuItem {
  type: "button";
  id: ConversationMenuItemId;
  label: string;
  icon: MaterialSymbol;
  disabled?: boolean;
  onClick: () => void;
  iconProps?: ComponentProps<typeof Icon>;
}

interface SeparatorConversationMenuItem {
  type: "separator";
}

export type ConversationMenuItem = ButtonConversationMenuItem | SeparatorConversationMenuItem;

export interface UseConversationMenuOptions {
  conversation?: MaybePartialInboxConversation | null;
  draftPlacement?: ReplyPlacement;
}

export interface UseConversationMenuReturnValue {
  menuItems: ConversationMenuItem[];
  isLoading: boolean;
}

const pendingGroupsByConversationId = new Map<string, Set<ConversationMenuPendingGroup>>();
const pendingListeners = new Set<() => void>();

const notifyPendingListeners = () => {
  pendingListeners.forEach((listener) => listener());
};

const subscribeToPendingGroups = (listener: () => void) => {
  pendingListeners.add(listener);

  return () => {
    pendingListeners.delete(listener);
  };
};

const getPendingGroupsSnapshot = (conversationId?: string) =>
  conversationId ? [...(pendingGroupsByConversationId.get(conversationId) ?? [])].sort().join("|") : "";

const hasPendingGroup = (conversationId: string, group: ConversationMenuPendingGroup) =>
  pendingGroupsByConversationId.get(conversationId)?.has(group) ?? false;

const setPendingGroup = (conversationId: string, group: ConversationMenuPendingGroup, pending: boolean) => {
  const pendingGroups = pendingGroupsByConversationId.get(conversationId) ?? new Set<ConversationMenuPendingGroup>();

  if (pending) {
    pendingGroups.add(group);
    pendingGroupsByConversationId.set(conversationId, pendingGroups);
  } else {
    pendingGroups.delete(group);

    if (pendingGroups.size) {
      pendingGroupsByConversationId.set(conversationId, pendingGroups);
    } else {
      pendingGroupsByConversationId.delete(conversationId);
    }
  }

  notifyPendingListeners();
};

const runPendingAction = (
  conversationId: string,
  group: ConversationMenuPendingGroup,
  action: () => Promise<unknown> | unknown
) => {
  if (hasPendingGroup(conversationId, group)) {
    return Promise.resolve();
  }

  setPendingGroup(conversationId, group, true);

  return Promise.resolve()
    .then(action)
    .finally(() => setPendingGroup(conversationId, group, false));
};

export const useCoversationMenu = ({
  conversation,
  draftPlacement = ReplyPlacement.floating,
}: UseConversationMenuOptions): UseConversationMenuReturnValue => {
  const { userInbox } = useAccount();
  const [addLabel, { loading: isAddLabelLoading }] = useAddLabelToConversation();
  const [removeLabel, { loading: isRemoveLabelLoading }] = useRemoveLabelFromConversation();
  const [deleteConversation, { loading: isDeleteConversationLoading }] = useDeleteConversation();
  const [createReplyDraft, { loading: isCreateReplyDraftLoading }] = useCreateReplyDraft();
  const [createForwardDraft, { loading: isCreateForwardDraftLoading }] = useCreateForwardDraft();
  const [setThreadReadState, { loading: isSetThreadReadStateLoading }] = useSetThreadReadState();

  const pendingGroupsSnapshot = useSyncExternalStore(
    subscribeToPendingGroups,
    () => getPendingGroupsSnapshot(conversation?.id),
    () => ""
  );
  const pendingGroups = useMemo(
    () => new Set(pendingGroupsSnapshot ? pendingGroupsSnapshot.split("|") : []),
    [pendingGroupsSnapshot]
  );
  const isCreatingDraft = isCreateReplyDraftLoading || isCreateForwardDraftLoading || pendingGroups.has("draft");
  const isUpdatingLabels = isAddLabelLoading || isRemoveLabelLoading || pendingGroups.has("label");
  const isDeleting = isDeleteConversationLoading || pendingGroups.has("delete");
  const isSettingReadState = isSetThreadReadStateLoading || pendingGroups.has("read");
  const isLoading = isCreatingDraft || isUpdatingLabels || isDeleting || isSettingReadState;
  // Own-inbox conversations are always replyable; a conversation in another user's inbox is replyable
  // only when the user actively collaborates on its deal (`viewerCanReplyAsCollaborator`). Both the
  // full conversation fragment and the inbox list fragment select it, so collaborated deals expose
  // reply/forward in the list too; the `in` guard keeps any other partial shape read-only.
  const isOwnInbox = conversation?.inboxId === userInbox?.id;
  const canReplyAsCollaborator =
    !!conversation && "viewerCanReplyAsCollaborator" in conversation && conversation.viewerCanReplyAsCollaborator;
  const isReadOnly = !isOwnInbox && !canReplyAsCollaborator;
  const createPendingAction = useCallback(
    (group: ConversationMenuPendingGroup, action: () => Promise<unknown> | unknown) => () => {
      if (!conversation) {
        return;
      }

      void runPendingAction(conversation.id, group, action);
    },
    [conversation]
  );

  const menuItems = useMemo(() => {
    if (!conversation || isReadOnly) {
      return [];
    }

    const { externalId, labels } = conversation;
    const isUnread = labels.includes(KnownInboxLabel.UNREAD);
    // A collaborator's read state lives in their own receipt, surfaced as `viewerIsUnread`; the
    // owner's `UNREAD` label is the fallback when the copy predates a receipt (or on a partial shape
    // that doesn't select the field).
    const viewerIsUnread = "viewerIsUnread" in conversation ? conversation.viewerIsUnread : isUnread;
    // The receipt path needs the thread's cross-inbox identity to write to; a collaborated copy that
    // predates stitching has none, so `useSetThreadReadState` would no-op. Gate the collaborator
    // read/unread control on it rather than surfacing a button that silently does nothing.
    const teamConversationId = "teamConversationId" in conversation ? conversation.teamConversationId : null;
    const isInbox = labels.includes(KnownInboxLabel.INBOX);
    const isStarred = labels.includes(KnownInboxLabel.STARRED);
    const isTrash = labels.includes(KnownInboxLabel.TRASH);

    // Reference the newest message the user actually sees. The detail view unions a collaborated
    // deal's stitched copies, so the newest message can live in a sibling mailbox; `messageId` pins
    // the draft to it rather than the newest message in the local copy. Undefined on the list
    // fragment (no messages) → the backend falls back to `conversationId`.
    const referenceMessageId = "messages" in conversation ? latestThreadMessageId(conversation.messages) : undefined;

    const items = [];

    items.push(
      {
        type: "button" as const,
        id: "reply" as const,
        label: "Reply",
        icon: "reply" as const,
        disabled: isCreatingDraft,
        onClick: createPendingAction("draft", () =>
          createReplyDraft(
            { conversationId: conversation.id, messageId: referenceMessageId },
            { placement: draftPlacement }
          )
        ),
      },
      {
        type: "button" as const,
        id: "reply-all" as const,
        label: "Reply all",
        icon: "reply_all" as const,
        disabled: isCreatingDraft,
        onClick: createPendingAction("draft", () =>
          createReplyDraft(
            { conversationId: conversation.id, messageId: referenceMessageId, replyAll: true },
            { placement: draftPlacement }
          )
        ),
      },
      {
        type: "button" as const,
        id: "forward" as const,
        label: "Forward",
        icon: "forward" as const,
        disabled: isCreatingDraft,
        onClick: createPendingAction("draft", () =>
          createForwardDraft(
            { conversationId: conversation.id, messageId: referenceMessageId },
            { placement: draftPlacement }
          )
        ),
      }
    );

    // Mailbox mutations (archive/move, delete, read-state, star) resolve against the viewer's own
    // inbox on the backend — `add/removeLabelToConversation` and `deleteConversation` all filter on
    // `inboxId = viewer.inboxId` and throw otherwise — so they only work on own-inbox threads. A
    // collaborator's foreign-inbox row therefore exposes reply/forward only; surfacing these would
    // offer controls that fail on the collaborated copy.
    if (isOwnInbox) {
      items.push({ type: "separator" as const });

      if (isInbox) {
        items.push({
          type: "button" as const,
          id: "inbox" as const,
          label: "Archive",
          icon: "inventory_2" as const,
          disabled: isUpdatingLabels,
          onClick: createPendingAction("label", () => removeLabel(conversation, KnownInboxLabel.INBOX)),
        });
      } else {
        items.push({
          type: "button" as const,
          id: "inbox" as const,
          label: "Move to inbox",
          icon: "move_to_inbox" as const,
          disabled: isUpdatingLabels,
          onClick: createPendingAction("label", () => addLabel(conversation, KnownInboxLabel.INBOX)),
        });
      }

      if (isTrash) {
        items.push({
          type: "button" as const,
          id: "delete" as const,
          label: "Delete forever",
          icon: "delete" as const,
          disabled: isDeleting,
          onClick: createPendingAction("delete", () => deleteConversation(conversation.id)),
        });
      } else {
        items.push({
          type: "button" as const,
          id: "delete" as const,
          label: "Delete",
          icon: "delete" as const,
          disabled: isUpdatingLabels,
          onClick: createPendingAction("label", () => addLabel(conversation, KnownInboxLabel.TRASH)),
        });
      }

      if (isUnread) {
        items.push({
          type: "button" as const,
          id: "read-state" as const,
          label: "Mark as read",
          icon: "drafts" as const,
          disabled: isUpdatingLabels,
          onClick: createPendingAction("label", () => removeLabel(conversation, KnownInboxLabel.UNREAD)),
        });
      } else {
        items.push({
          type: "button" as const,
          id: "read-state" as const,
          label: "Mark as unread",
          icon: "mark_email_unread" as const,
          disabled: isUpdatingLabels,
          onClick: createPendingAction("label", () => addLabel(conversation, KnownInboxLabel.UNREAD)),
        });
      }

      if (isStarred) {
        items.push({
          type: "button" as const,
          id: "star" as const,
          label: "Remove star",
          icon: "star" as const,
          disabled: isUpdatingLabels,
          onClick: createPendingAction("label", () => removeLabel(conversation, KnownInboxLabel.STARRED)),
          iconProps: { className: "filled" } as ButtonConversationMenuItem["iconProps"],
        });
      } else {
        items.push({
          type: "button" as const,
          id: "star" as const,
          label: "Add star",
          icon: "star" as const,
          disabled: isUpdatingLabels,
          onClick: createPendingAction("label", () => addLabel(conversation, KnownInboxLabel.STARRED)),
        });
      }
    } else if (teamConversationId) {
      // Collaborator (foreign-inbox) copy: the label mutations above throw against the owner's copy,
      // so read/unread routes to the per-user receipt path instead, which writes only the viewer's
      // own state and never the owner's row or Gmail. Only surfaced when the copy carries a
      // `teamConversationId` — without it the receipt has nowhere to land and the control would no-op.
      items.push({ type: "separator" as const });

      if (viewerIsUnread) {
        items.push({
          type: "button" as const,
          id: "read-state" as const,
          label: "Mark as read",
          icon: "drafts" as const,
          disabled: isSettingReadState,
          onClick: createPendingAction("read", () => setThreadReadState(conversation, true)),
        });
      } else {
        items.push({
          type: "button" as const,
          id: "read-state" as const,
          label: "Mark as unread",
          icon: "mark_email_unread" as const,
          disabled: isSettingReadState,
          onClick: createPendingAction("read", () => setThreadReadState(conversation, false)),
        });
      }
    }

    // TODO: Implement custom labels
    // items.push(
    //   {
    //     type: "separator" as const,
    //   },
    //   {
    //     icon: "label",
    //     label: "Label as",
    //     onClick: () => {},
    //   },
    //   {
    //     icon: "move_item",
    //     label: "Move to",
    //     onClick: () => {},
    //   }
    // );

    if (externalId) {
      items.push(
        { type: "separator" as const },
        {
          type: "button" as const,
          id: "open-in-gmail" as const,
          label: "Open in Gmail",
          icon: "open_in_browser" as const,
          onClick: () =>
            window.open(`https://mail.google.com/mail/u/0/#all/${encodeURIComponent(externalId)}`, "gmail"),
        }
      );
    }

    return items;
  }, [
    conversation,
    isCreatingDraft,
    isDeleteConversationLoading,
    isReadOnly,
    isUpdatingLabels,
    isDeleting,
    isSettingReadState,
    draftPlacement,
    addLabel,
    removeLabel,
    setThreadReadState,
    createReplyDraft,
    createForwardDraft,
    deleteConversation,
    createPendingAction,
  ]);

  const value: UseConversationMenuReturnValue = useMemo(
    () => ({
      menuItems,
      isLoading,
    }),
    [menuItems, isLoading]
  );

  return value;
};
